import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import { Link, useLocation, useParams } from 'react-router';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import {
  SvgAddRegular,
  SvgSubtractRegular,
  TextV2,
  Alert,
} from '@insights-ltd/design-library/components';
import { queryStatus } from 'utils/queryStatus';
import { QueryStatus, useQueryClient } from '@tanstack/react-query';
import {
  ArchivedProfileResponse,
  emailProfile,
  LearnerPurchaseRequest,
  useGetAllLearnerEvents,
  useGetArchivedProfilesByOrganisation,
  useGetLearnerByOrganisation,
  useGetLearnerTeams,
  useGetOrganisation,
  useGetProfilesByOrganisation,
  usePurchaseLearnerChapter,
} from 'api';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import FullHeight from 'components/FullHeight';
import Breadcrumbs from 'components/Breadcrumbs';
import { useGetAllOrganisationsAndGroups } from 'components/hooks/useGetAllOrganisationsAndGroups';
import PurchasePage from 'components/PurchasePage/PurchasePage';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import useIsPermitted from 'components/hooks/useIsPermitted';
import ProfileShareNewLinkDialog from 'components/ProfileShareNewLinkDialog';
import EmailProgressDialog from 'components/EmailProgressDialog';
import { ProfileType } from 'types/types';
import LearnerInfo from './LearnerInfo';
import OrganisationInfo from './OrganisationInfo';
import LearnerExperiences from './LearnerExperiences';
import Profiles from './Profiles';
import ProfileArchivedCard from './ProfileArchivedCard';
import { LearnerTeamsList } from './LearnerTeams';
import { getPurchaseEvaluationId, truncatedTextStyles } from './learnerHelper';

type LearnerProps = {
  otherStatus?: QueryStatus;
  crumbs?: Record<string, string>;
  evaluatorLinkId?: string;
};

const Learner = ({ otherStatus, crumbs, evaluatorLinkId }: LearnerProps) => {
  const { isPermitted: canArchiveProfiles } = useIsPermitted([
    { action: 'PurchaseProfiles', scope: 'Local' },
  ]);
  const [downloadProfileError, setDownloadProfileError] = useState(false);
  const [archivedProfilesExpanded, setArchivedProfilesExpanded] =
    React.useState<string | false>(false);
  const [restoredProfile, setRestoredProfile] = useState<
    ArchivedProfileResponse | undefined
  >();
  const [restoredProfileTitle, setRestoredProfileTitle] = useState<string>('');
  const [restoredProfileType, setRestoredProfileType] = useState<
    ProfileType | ''
  >('');
  const [shareNewLinkError, setShareNewLinkError] = useState<boolean>(false);
  const [sendingProfile, setSendingProfile] = useState(false);
  const [showShareNewLinkDialog, setShowShareNewLinkDialog] = useState(false);
  const { learnerId, organisationId } = useParams<{
    learnerId: string;
    organisationId: string;
  }>();
  const { t } = useTranslation();
  const location = useLocation();
  const { status: learnerStatus, data: learner } = useGetLearnerByOrganisation(
    learnerId || '',
    organisationId || '',
  );
  const {
    status: orgsStatus,
    organisations: practitionerOrganisations,
    organisationsInGroups,
  } = useGetAllOrganisationsAndGroups();
  const { status: orgStatus, data: organisation } =
    useGetOrganisation(organisationId);
  const { status: eventStatus, data: events } = useGetAllLearnerEvents(
    learnerId || '',
  );
  const { status: profileStatus, data: profiles } =
    useGetProfilesByOrganisation(learnerId || '', organisationId || '');
  const { status: archivedProfileStatus, data: archivedProfiles } =
    useGetArchivedProfilesByOrganisation(learnerId || '', organisationId || '');
  const { status: learnerTeamsStatus, data: learnerTeams } = useGetLearnerTeams(
    learnerId ?? '',
  );

  useEffect(() => {
    if (archivedProfiles && archivedProfiles.length > 0) {
      setArchivedProfilesExpanded(
        profiles && profiles.length === 0 ? 'panel1' : false,
      );
    }
  }, [archivedProfiles, profiles]);

  const StyledAddIcon = styled(SvgAddRegular)(({ theme }) => ({
    width: 12,
    height: 12,
    stroke: theme.palette.primary.main,
  }));

  const StyledSubtractIcon = styled(SvgSubtractRegular)(({ theme }) => ({
    width: 12,
    height: 12,
    stroke: theme.palette.primary.main,
  }));

  const RestrictedUserDialog = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.blue.light,
    fontWeight: 800,
    padding: '15px',
    margin: '0 0 15px 0',
  }));

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setArchivedProfilesExpanded(isExpanded ? panel : false);
    };

  const status = queryStatus(
    learnerTeamsStatus,
    eventStatus,
    learnerStatus,
    profileStatus,
    archivedProfileStatus === 'pending' ? 'success' : archivedProfileStatus,
    otherStatus || 'success',
    orgStatus,
    orgsStatus,
  );

  const showOrgs: boolean =
    practitionerOrganisations && practitionerOrganisations?.length > 1;

  const defaultCrumbs: Record<string, string> = {
    '/': t('ui.event-management.dashboard.home.title'),
    '/learners': t('ui.event-management.learners-and-teams.title'),
  };

  const breadcrumbs: Record<string, string> =
    crumbs === undefined ? defaultCrumbs : crumbs;

  const allValidEvents = events ?? [];

  const parentLink = `/learners/${organisation?.id}/${learnerId}`;
  const editLearnerLink = evaluatorLinkId
    ? `/evaluator-links/${evaluatorLinkId}/${organisationId}/${learnerId}/edit`
    : `/learners/${organisationId}/${learnerId}/edit`;
  const queryClient = useQueryClient();
  const {
    mutate,
    error,
    isError,
    isPending,
    reset: resetMutate,
  } = usePurchaseLearnerChapter(queryClient);
  const pageTitle = t(
    'ui.event-management.learners.purchase-profiles.page-title',
  );

  const handlePurchase = (
    requestData: LearnerPurchaseRequest,
    onSuccess: () => void,
  ) => {
    const { createProfiles, updateProfiles, product } = requestData;
    return mutate(
      {
        learnerId: learnerId ?? '',
        organisationId: organisation?.id ?? '',
        purchaseData: {
          product,
          createProfile: createProfiles[0],
          updateProfile: updateProfiles[0],
        },
      },
      { onSuccess },
    );
  };

  if (status === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }

  if (status === 'error' || !learner || !organisation || !profiles) {
    return (
      <FullScreenError
        message={t('ui.event-management.learner.error-loading-learner')}
      />
    );
  }

  const purchaseEvaluationId = getPurchaseEvaluationId(
    location.state,
    profiles,
    learner.latestEvaluation?.id,
  );

  const linkCrumbs: Record<string, string> = {
    '/': t('ui.event-management.events.nav.home'),
    '/organisations': t('ui.event-management.organisations.title'),
    [`/organisations/${organisation.id}`]: organisation.name,
    [parentLink]: learner.fullName,
  };

  const handleShareNewLink = async () => {
    try {
      await emailProfile(
        restoredProfile?.id || '',
        restoredProfile?.preferredProfileDialect || 'en-GB',
        'share-digital-profile-link',
      );
      setSendingProfile(true);
      setShowShareNewLinkDialog(false);
    } catch {
      setShareNewLinkError(true);
    }
  };

  const handleShareNewLinkClose = () => {
    setShowShareNewLinkDialog(false);
    setShareNewLinkError(false);
  };

  return (
    <>
      <Helmet>
        <title>
          {t('ui.event-management.title.view-learner', {
            learnerName: learner.fullName,
          })}
        </title>
      </Helmet>
      <FullHeight backgroundColor="white">
        <PurchasePage
          onPurchase={handlePurchase}
          title={pageTitle}
          breadcrumbs={linkCrumbs}
          indexRoute={
            <Container maxWidth="lg">
              <Box
                sx={{
                  py: (theme) => theme.spacing(spacingSizeMap.M),
                }}
              >
                <Breadcrumbs
                  crumbs={breadcrumbs}
                  activeText={learner.fullName}
                  sx={{
                    '& .MuiBreadcrumbs-li > p': {
                      ...truncatedTextStyles,
                    },
                  }}
                />
              </Box>
              <Grid container>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Box
                    sx={{
                      ...truncatedTextStyles,
                      maxWidth: '100%',
                    }}
                  >
                    <TextV2 component="h1" variant="h2">
                      {learner.fullName}
                    </TextV2>
                  </Box>
                </Grid>
                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{
                    display: {
                      md: 'flex',
                    },

                    justifyContent: {
                      md: 'flex-end',
                    },
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    to={editLearnerLink}
                  >
                    {t('ui.event-management.learner.edit-button')}
                  </Button>
                </Grid>
              </Grid>
              <Box
                sx={{
                  mb: (theme) => theme.spacing(spacingSizeMap.L),
                }}
              />
              <Divider />
              <Grid container spacing={2}>
                <Grid container spacing={4} size={8}>
                  <LearnerInfo learner={learner} />
                  <Grid
                    size={12}
                    sx={{
                      mt: '-0.5rem',
                    }}
                  >
                    <OrganisationInfo
                      learnerId={learnerId || ''}
                      currentOrg={organisation}
                      name={organisation?.name || ''}
                      dataRetentionPolicy={organisation?.dataRetentionPolicy}
                      organisations={practitionerOrganisations}
                      parent={
                        organisationId
                          ? organisationsInGroups[organisationId]
                          : undefined
                      }
                    />
                  </Grid>
                </Grid>
                <Grid
                  size={12}
                  sx={{
                    mb: (theme) => theme.spacing(spacingSizeMap.L),
                  }}
                >
                  <Profiles
                    learnerId={learnerId || ''}
                    profiles={profiles}
                    evaluatorSubmittedOn={
                      learner?.latestEvaluation?.submittedOn
                    }
                    onProfileDownloadError={() => setDownloadProfileError(true)}
                    organisationId={organisationId || ''}
                    latestEvaluation={learner?.latestEvaluation}
                  />
                  {archivedProfiles && archivedProfiles.length !== 0 && (
                    <>
                      <Box
                        sx={{
                          pb: '24px',
                        }}
                      />
                      <Accordion
                        expanded={archivedProfilesExpanded === 'panel1'}
                        onChange={handleChange('panel1')}
                        sx={(theme) => ({
                          backgroundColor: 'transparent',
                          margin: '0 !important',
                          boxShadow: 'none',
                          '&.MuiAccordion-root': { position: 'inherit' },
                          '& .MuiAccordionSummary-root': {
                            padding: 0,
                            fontWeight: 600,
                            fontSize: '1rem',
                            lineHeight: '1.625rem',
                            color: theme.palette.blue.main,
                          },
                          '& .MuiAccordionSummary-expandIconWrapper > svg': {
                            strokeWidth: 3,
                          },
                        })}
                      >
                        <AccordionSummary
                          expandIcon={
                            archivedProfilesExpanded === 'panel1' ? (
                              <StyledSubtractIcon />
                            ) : (
                              <StyledAddIcon />
                            )
                          }
                          aria-controls="panel1d-content"
                          id="panel1d-header"
                          sx={{
                            width: 'fit-content',
                            minHeight: '1px !important',
                            '& .MuiAccordionSummary-content.Mui-expanded': {
                              margin: 0,
                            },
                            '& .MuiAccordionSummary-content': {
                              margin: 0,
                              paddingRight: '5px',
                            },
                          }}
                        >
                          {t(
                            'ui.event-management.learner.profiles.archived.profiles',
                            {
                              numberOfArchivedProfiles: archivedProfiles.length,
                            },
                          )}
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            '&.MuiAccordionDetails-root': {
                              padding: '1rem 0 0 0 !important',
                            },
                          }}
                        >
                          {!canArchiveProfiles && (
                            <RestrictedUserDialog>
                              {t(
                                'ui.event-management.learner.profiles.restore.restricted-user.permission.warning',
                              )}
                            </RestrictedUserDialog>
                          )}
                          <Grid
                            container
                            spacing={2}
                            data-testid="archived-profiles"
                          >
                            {archivedProfiles.map((profile) => (
                              <Grid key={profile.id} size={{ xs: 12, sm: 4 }}>
                                <ProfileArchivedCard
                                  learnerId={learnerId || ''}
                                  profile={profile}
                                  organisationId={organisationId || ''}
                                  setRestoredProfile={setRestoredProfile}
                                  setRestoredProfileTitle={
                                    setRestoredProfileTitle
                                  }
                                  setRestoredProfileType={
                                    setRestoredProfileType
                                  }
                                  setShowShareNewLinkDialog={
                                    setShowShareNewLinkDialog
                                  }
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </>
                  )}
                  <Box
                    sx={{
                      mb: '40px',
                    }}
                  />
                  <LearnerExperiences
                    events={allValidEvents}
                    showOrgs={showOrgs}
                  />
                  <Box
                    sx={{
                      pb: '24px',
                    }}
                  />
                  <LearnerTeamsList learnerTeams={learnerTeams} />
                </Grid>
              </Grid>
            </Container>
          }
          evaluationIds={purchaseEvaluationId ? [purchaseEvaluationId] : []}
          parentLink={parentLink}
          isError={isError}
          isPurchasing={isPending}
          error={error}
          onReset={resetMutate}
          organisationIds={[organisation?.id ?? '']}
          type="Chapters"
        />
        <Snackbar
          open={downloadProfileError}
          autoHideDuration={5000}
          onClose={() => setDownloadProfileError(false)}
        >
          <Alert elevation={6} severity="error">
            {t('ui.event-management.generic.error')}
          </Alert>
        </Snackbar>

        <ProfileShareNewLinkDialog
          open={
            showShareNewLinkDialog && restoredProfileType == 'EXPLORE_PROFILE'
          }
          profileTitle={restoredProfileTitle}
          onCancel={handleShareNewLinkClose}
          onSubmit={handleShareNewLink}
          shareNewLinkError={shareNewLinkError}
        />

        <EmailProgressDialog
          singleProfile
          open={sendingProfile}
          onClose={() => setSendingProfile(false)}
        />
      </FullHeight>
    </>
  );
};

export default Learner;
